import { unit } from 'mathjs';
// import frLocale from 'date-fns/locale/fr';
// import enLocale from 'date-fns/locale/en-US';
import { unitMasks } from 'components/units/UnitDefinitionsAndMasks';
import { signOut } from 'firebase/auth';

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export async function isUserAdmin(user) {
  if (user !== null) {
    const token = await user.getIdTokenResult();
    if (token?.claims.admin !== undefined) {
      return token.claims.admin === 'true';
    }
  }
  return false;
}

export async function isAlpha(user) {
  const token = await user.getIdTokenResult();
  if (token?.claims.alpha !== undefined) {
    return token.claims.alpha === 'true';
  }
  return false;
}

export function inAdminPage(location) {
  return location.pathname.includes('admin');
}

export function round(value, decimals) {
  return roundNoFixed(value, decimals).toFixed(decimals);
}

export function roundNoFixed(value, decimals) {
  const decimalFix = `${1}e${decimals}`;
  return Number(Math.round((value + Number.EPSILON) * decimalFix) / decimalFix);
}

export function useQuery(useMemo, useLocation) {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function searchInCompartmentArray(value, indexArray, valuesDict) {
  const exactMatch = valuesDict[value];
  if (exactMatch !== undefined) {
    return exactMatch;
  }
  const closestIndexInf = indexArray.reduce((prev, curr) => (curr < value && curr > prev ? curr : prev));
  const closestIndexSup = indexArray[indexArray.indexOf(closestIndexInf) + 1];

  const closestValueSup = valuesDict[closestIndexInf];
  const closestValueInf = valuesDict[closestIndexSup];
  const infToSupDistanceProportion = (value - closestIndexInf) / (closestIndexSup - closestIndexInf);
  const newValue = closestValueSup
    - infToSupDistanceProportion * (closestValueSup - closestValueInf);
  return newValue;
}

export function downloadCsvFile(name, csv) {
  const hiddenElement = document.createElement('a');
  hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
  hiddenElement.target = '_blank';
  hiddenElement.download = `${name}_${new Date().toLocaleDateString()}.csv`;
  hiddenElement.click();
}

export function nominalVolumeFromCompartment(comp, t) {
  if (comp.nominalVolume === undefined || comp.nominalVolume === null) {
    return t('operation.not_known');
  }
  return roundNoFixed(
    unit(comp.nominalVolume, 'L').toNumber('hL'),
    unitMasks.hL,
  );
}

// export function localeFinder(locale) {
//   const localeMap = {
//     en: enLocale,
//     fr: frLocale,
//   };
//   return localeMap[locale];
// }

export function replaceComma(value) {
  if (value) {
    return value.toString().replace(/,/, '.');
  }
  return value;
}

export function navigatorNetworkState() {
  // console.log('Navigator online: ', navigator.onLine);
  return navigator.onLine ? 'online' : 'offline';
}

const knownDatabases = ['firebase-heartbeat-database',
  'firebase-installations-database', 'firebaseLocalStorageDb',
  'firebase_remote_config', 'firestore/[DEFAULT]/labox-ws/main'];

const clearIndexedDB = async () => {
  if (window.indexedDB.databases) {
    const databases = await window.indexedDB.databases();
    await Promise.all(databases.map((database) => {
      return new Promise((resolve, reject) => {
        const deleteRequest = window.indexedDB.deleteDatabase(database.name);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
          // If blocked, wait a bit and try to resolve anyway
          setTimeout(() => resolve(), 100);
        };
      });
    }));
  } else {
    // Fallback to predefined list
    await Promise.all(knownDatabases.map((dbName) => {
      return new Promise((resolve, reject) => {
        const deleteRequest = window.indexedDB.deleteDatabase(dbName);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
          setTimeout(() => resolve(), 100);
        };
      });
    }));
  }
};

// eslint-disable-next-line no-promise-executor-return
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const refreshSessionAndReload = () => {
  sessionStorage.clear();
  window.location.reload();
};

export const refreshSWAndReload = async () => {
  sessionStorage.clear();
  // This will directly look for the service worker if it is
  // waiting and skip it then reload the page
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((readyRegistration) => {
      if (readyRegistration.waiting) {
        readyRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    });
  }
  await sleep(2000);
  window.location.reload();
};

async function clearCacheStorage() {
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

export const clearApplicationCache = async (auth, globalActions) => {
  // Step 1: Sign out the user to properly disconnect Firebase Auth
  // This ensures Firebase Auth state is cleared before we delete IndexedDB
  if (auth) {
    try {
      if (auth.currentUser) {
        await signOut(auth);
        // Small delay to ensure Firebase Auth state is fully cleared
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      // If signOut fails, log but continue with cache clearing
      console.warn('Failed to sign out user before clearing cache:', error);
    }
  }

  // Step 2: Reset global state if provided
  if (globalActions) {
    try {
      globalActions.setUserStatus('initial');
      globalActions.setActiveOrganization('');
      globalActions.setClientPath('');
      globalActions.setUserPlan('');
      globalActions.setUserRoles([]);
      globalActions.setUserWSRoles([]);
      globalActions.setNetworkState('online');
    } catch (error) {
      console.warn('Failed to reset global state:', error);
    }
  }

  // Step 3: Clear browser storage (localStorage, sessionStorage, cookies)
  localStorage.clear();
  sessionStorage.clear();

  // Clear cookies
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    if (name) {
      // Clear cookie for current domain and parent domains
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    }
  }

  // Step 4: Clear IndexedDB (includes Firebase Auth persistence and Firestore cache)
  try {
    await clearIndexedDB();
  } catch (error) {
    console.warn('Error clearing IndexedDB:', error);
  }

  // Step 5: Clear Cache Storage (service worker caches)
  try {
    await clearCacheStorage();
  } catch (error) {
    console.warn('Error clearing cache storage:', error);
  }

  // Step 6: Reload the page to start fresh
  await refreshSWAndReload();
};

// UNUSED METHOD COMMENTED BELOW
// MIGHT STILL BE USEFUL SOMEDAY DEPENDING ON APP PERFORMANCE REGARDING OFFLINE MODE

// async function fetchWithTimeout(resource, options = {}) {
//   const { timeout = 8000 } = options;

//   const controller = new AbortController();
//   const id = setTimeout(() => controller.abort(), timeout);
//   const response = await fetch(resource, {
//     ...options,
//     signal: controller.signal,
//   });
//   clearTimeout(id);
//   return response;
// }

// export async function fetchNetworkState(functions) {
//   try {
//     const response = await fetchWithTimeout('http://neverssl.com', {
//       timeout: 6000,
//     });
//     return response.status >= 200 && response.status < 300 ? 'online' : 'offline';
//   } catch (err) {
//     return 'offline';
//   }
// }

// export function navigatorNetworkState2() {
//   // This isn't reliable for a lot of devices
// see: https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
//   // That is why we will use fetchNetworkState to make sure at app start
//   const connection = navigator.connection
//     || navigator.mozConnection
//     || navigator.webkitConnection
//     || navigator.webkitConnection;

//   if (connection?.downlink <= 0.1) {
//     return 'offline';
//   }

//   // This is less precise and for iOS support
//   if (navigator.onLine === false) {
//     return 'offline';
//   }

//   return 'online';
// }
